/**
 * Help inbox API schemas (epic PI-SPEC-SW-E302, ER-1655, contract help 1;
 * Resolved Decision 5).
 *
 * After the person confirms "Send to Aaron", the Help panel sends the
 * conversation, the page context, an optional note and up to
 * {@link HELP_IMAGES_MAX} screenshots to SpecTree in one multipart request.
 * Only global Admins read the inbox; senders never see other people's
 * reports. A report stays until an Admin marks it handled. Unsent
 * conversations are never stored, and nothing is sent to external systems.
 *
 * Covered routes, under {@link HELP_INBOX_PATH}:
 * - `POST /`                          one report (multipart: the `report`
 *                                     JSON field, {@link helpInboxReportCreateSchema},
 *                                     plus 0-3 `screenshot` files)
 * - `GET  /?status=open|handled|all`  the inbox (Admins)
 * - `GET  /count`                     open reports (Admins)
 * - `GET  /:id`                       one report (Admins)
 * - `GET  /:id/screenshots/:index`    one screenshot's bytes (Admins)
 * - `PATCH /:id`                      mark handled or open again (Admins)
 *
 * Request schemas are strict (unknown keys are refused). Response schemas
 * are lenient, so a SpecTree release may add response fields without a
 * contract bump. Every response is wrapped as `{ data: ... }`; parse the
 * envelope with `apiDataSchema` from the grooming module.
 */
import { z } from "zod";
import { isoDateTime } from "../zod-helpers.js";
import { HELP_IMAGE_BYTES_MAX, HELP_IMAGE_MIME_TYPES, HELP_IMAGE_NAME_MAX, HELP_IMAGES_MAX, HELP_INBOX_MESSAGES_MAX, HELP_MESSAGE_MAX, HELP_MESSAGE_ROLES, HELP_NOTE_MAX, HELP_SOURCES_MAX, } from "./constants.js";
import { helpContextSchema, helpSourceSchema } from "./types.js";
// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------
/** Path prefix of the Help inbox routes. */
export const HELP_INBOX_PATH = "/api/v1/toolbox/help-inbox";
/** Multipart field that carries the report JSON on `POST` {@link HELP_INBOX_PATH}. */
export const HELP_INBOX_REPORT_FIELD = "report";
/** Multipart field name of each screenshot file on `POST` {@link HELP_INBOX_PATH}. */
export const HELP_INBOX_SCREENSHOT_FIELD = "screenshot";
/** Which reports the inbox lists. */
export const HELP_INBOX_STATUSES = ["open", "handled", "all"];
/** Paths of the routes this module covers, relative to the SpecTree origin. */
export const helpInboxApiPaths = {
    create: HELP_INBOX_PATH,
    list: (status) => status === undefined ? HELP_INBOX_PATH : `${HELP_INBOX_PATH}?status=${encodeURIComponent(status)}`,
    count: `${HELP_INBOX_PATH}/count`,
    report: (id) => `${HELP_INBOX_PATH}/${encodeURIComponent(id)}`,
    screenshot: (id, index) => `${HELP_INBOX_PATH}/${encodeURIComponent(id)}/screenshots/${String(index)}`,
};
// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------
const isoDateTimeSchema = isoDateTime({ offset: true });
/** One message of the conversation sent to the inbox; Help messages may carry their sources. */
export const helpInboxMessageSchema = z
    .object({
    role: z.enum(HELP_MESSAGE_ROLES),
    text: z.string().max(HELP_MESSAGE_MAX),
    sources: z.array(helpSourceSchema).max(HELP_SOURCES_MAX).optional(),
})
    .strict();
/**
 * The `report` JSON of `POST` {@link HELP_INBOX_PATH}: the person's optional
 * note, the conversation (at least one message), the page context and
 * whether Help said it was not sure. The sender is the signed-in caller,
 * never a field.
 */
export const helpInboxReportCreateSchema = z
    .object({
    note: z.string().trim().max(HELP_NOTE_MAX).optional(),
    conversation: z.array(helpInboxMessageSchema).min(1).max(HELP_INBOX_MESSAGES_MAX),
    context: helpContextSchema,
    helpWasNotSure: z.boolean(),
})
    .strict();
/** The query of `GET` {@link HELP_INBOX_PATH}. Absent `status` means `open`. */
export const helpInboxListQuerySchema = z
    .object({ status: z.enum(HELP_INBOX_STATUSES).optional() })
    .strict();
/** The body of `PATCH /:id`: mark a report handled (`true`) or open again (`false`). */
export const helpInboxHandledSchema = z.object({ handled: z.boolean() }).strict();
// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------
/** Who sent a report. */
export const helpInboxSenderSchema = z
    .object({
    id: z.string(),
    name: z.string(),
    email: z.string().optional(),
})
    .loose();
/** One stored screenshot's metadata (the bytes come from `helpInboxApiPaths.screenshot`). */
export const helpInboxScreenshotSchema = z
    .object({
    index: z.number().int().min(0).max(HELP_IMAGES_MAX - 1),
    name: z.string().max(HELP_IMAGE_NAME_MAX),
    mimeType: z.enum(HELP_IMAGE_MIME_TYPES),
    sizeBytes: z.number().int().min(1).max(HELP_IMAGE_BYTES_MAX),
})
    .loose();
/** One report in the inbox list. */
export const helpInboxReportSummarySchema = z
    .object({
    id: z.string(),
    createdAt: isoDateTimeSchema,
    sender: helpInboxSenderSchema,
    /** The page the person was on. */
    route: z.string(),
    /** The person's first question, for the list row. */
    firstQuestion: z.string(),
    helpWasNotSure: z.boolean(),
    hasNote: z.boolean(),
    screenshotCount: z.number().int().min(0).max(HELP_IMAGES_MAX),
    handledAt: isoDateTimeSchema.nullable(),
    handledBy: helpInboxSenderSchema.nullable(),
})
    .loose();
/** `GET /:id` → `data`: the whole report. */
export const helpInboxReportDetailSchema = helpInboxReportSummarySchema
    .extend({
    note: z.string().nullable(),
    conversation: z.array(helpInboxMessageSchema.loose()),
    context: helpContextSchema.loose(),
    screenshots: z.array(helpInboxScreenshotSchema).max(HELP_IMAGES_MAX),
})
    .loose();
/** `GET` {@link HELP_INBOX_PATH} → `data`: the reports, newest first. */
export const helpInboxListResponseSchema = z
    .object({ reports: z.array(helpInboxReportSummarySchema) })
    .loose();
/** `GET /count` → `data`: how many reports are open (the rail badge). */
export const helpInboxCountSchema = z.object({ open: z.number().int().min(0) }).loose();
//# sourceMappingURL=api.js.map